import { on } from '@ember/modifier';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { FlashMessage } from 'ember-cli-flash';

import {
  TOAST_STATUS_MAP,
  type ToastStatus,
  type TrackedValue,
} from '../../services/toast.ts';
import type { IconName } from '../../types/components/prism/icon.ts';
import type { ToastAction } from '../../types/components/prism/toast-queue.ts';
import PrismButton from './button.gts';
import PrismIcon from './icon.gts';

const TOAST_STATUS_ICON_MAP = {
  [TOAST_STATUS_MAP.automationOn]: 'automation',
  [TOAST_STATUS_MAP.automationOff]: 'automation-off',
  [TOAST_STATUS_MAP.error]: 'circle-cross',
  [TOAST_STATUS_MAP.info]: 'circle-information-filled',
  [TOAST_STATUS_MAP.success]: 'circle-checkmark',
  [TOAST_STATUS_MAP.warning]: 'warning',
} as const;

const TOAST_STATUS_CLASS_MAP = {
  [TOAST_STATUS_MAP.automationOn]: 'automation-on',
  [TOAST_STATUS_MAP.automationOff]: 'automation-off',
  [TOAST_STATUS_MAP.error]: 'error',
  [TOAST_STATUS_MAP.info]: 'info',
  [TOAST_STATUS_MAP.success]: 'success',
  [TOAST_STATUS_MAP.warning]: 'warning',
} as const;

interface PrismToastSignature {
  Args: {
    action?: TrackedValue<ToastAction>;
    dismissible?: boolean;
    message?: string;
    onClose: () => void;
    status: ToastStatus;
    statusIcon?: boolean;
    /**
     * @deprecated The title property is deprecated. And will be removed in a future version. Please use the message property instead.
     */
    title?: string;
  };
}

class PrismToast extends Component<PrismToastSignature> {
  get action(): ToastAction | undefined {
    return this.args.action?.value;
  }

  get message(): string {
    return this.args.message ?? this.args.title ?? '';
  }

  get statusClass(): string {
    const { status } = this.args;

    return TOAST_STATUS_CLASS_MAP[status];
  }

  get statusIconName(): IconName {
    const { status } = this.args;

    return TOAST_STATUS_ICON_MAP[status];
  }

  <template>
    <div>
      <section
        class="prism-toast {{this.statusClass}}"
        data-test-id="prism-toast"
      >
        <aside class="prism-toast__status" data-test-id="prism-toast__status">
          <PrismIcon @color="inherit" @name={{this.statusIconName}} />
        </aside>

        <article class="prism-toast__body">
          <p
            class="prism-toast__message text-300--regular truncate"
            data-test-id="prism-toast__message"
          >
            {{this.message}}
          </p>
        </article>

        {{#if this.action}}
          <div class="prism-toast__action">
            <PrismButton
              @buttonType="ghost"
              @isDisabled={{this.action.isDisabled}}
              @isLoading={{this.action.isLoading}}
              @label={{this.action.label}}
              @size="small"
              data-test-id="prism-toast__action-button"
              {{on "click" this.action.onClick}}
            />
          </div>
        {{/if}}

        {{#if @dismissible}}
          {{#if this.action}}
            <div
              class="prism-toast__divider"
              data-test-id="prism-toast__divider"
            ></div>
          {{/if}}
          <button
            class="prism-toast__dismiss"
            data-test-id="prism-toast__dismiss"
            type="button"
            {{on "click" @onClose}}
          >
            <PrismIcon @color="inherit" @name="cross" @size="medium" />
          </button>
        {{/if}}
      </section>
    </div>
  </template>
}

interface PrismToastQueueSignature {
  Args: {};
}

export default class PrismToastQueue extends Component<PrismToastQueueSignature> {
  @service declare toast: Services['toast'];

  <template>
    <div class="prism-toast-queue" data-test-id="toast-queue">
      <div class="toast__wrapper">
        {{#each this.toast.queue as |flash|}}
          <FlashMessage @flash={{flash}} as |component flashObject onClose|>
            <PrismToast
              {{! @glint-expect-error: Property 'action' does not exist on type 'FlashObject'. }}
              @action={{flashObject.action}}
              {{! @glint-expect-error: Property 'dismissible' does not exist on type 'FlashObject'. }}
              @dismissible={{flashObject.dismissible}}
              {{! @glint-expect-error: Property 'message' does not exist on type 'FlashObject'. }}
              @message={{flashObject.message}}
              @onClose={{onClose}}
              {{! @glint-expect-error: Property 'status' does not exist on type 'FlashObject'. }}
              @status={{flashObject.status}}
              {{! @glint-expect-error: Property 'title' does not exist on type 'FlashObject'. }}
              @title={{flashObject.title}}
            />
          </FlashMessage>
        {{/each}}
      </div>
    </div>
  </template>
}
